/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { fromEvent, Subject, takeUntil, tap } from 'rxjs';

/**
 * PasteEvent like KeyboardEvent - to keep things typed.
 */
interface PasteEvent extends Event {
  clipboardData: any;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'cmg-decimal-input',
  templateUrl: './cmg-decimal-input.component.html',
  styleUrls: ['./cmg-decimal-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CmgDecimalInputComponent),
      multi: true,
    },
  ],
  imports: [InputGroupModule, InputGroupAddonModule, InputTextModule, CommonModule, ReactiveFormsModule],
})
export class CmgDecimalInputComponent implements AfterViewInit, OnInit, ControlValueAccessor, OnChanges, OnDestroy {
  private readonly _messageService = inject(MessageService);

  @Input()
  type: 'percent' | 'currency' | undefined = undefined;

  readonly placeholder = input<string>();

  readonly fractionDigits = input<number>();

  /**
   * roundToFractionDigits: If set to true, input values on writeValue will be rounded
   * to the correct number of decimal places based on fractionDigits Input
   */
  readonly roundToFractionDigits = input<boolean>();

  readonly min = input<number>();

  readonly max = input<number>();

  readonly id = input<string>();

  /* eslint-disable @angular-eslint/no-output-on-prefix */
  readonly onBlur = output<Event>();

  readonly theInput = viewChild<ElementRef>('theInnerInput');

  numberControl: FormControl;
  innerValue: any;
  disabled = false;
  private readonly _destroyed$ = new Subject<boolean>();
  private readonly _decimalSeparator: string = '.';
  private readonly _minFractionDigits: number = 0;
  private readonly _minusSign: string = '-';
  // value to use when user just clears the input
  private readonly _blankValue = null;

  private _integerUnsigned: RegExp;
  private _integerSigned: RegExp;
  private _decimalUnsigned: RegExp;
  private _decimalSigned: RegExp;
  private _allowedSymbols = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, '.', '-', 'Backspace', 'Delete'];
  private _regex: RegExp;
  private _previousValue;
  private get _allowSign() {
    const min = this.min();
    return (min && min < 0) || min === undefined;
  }

  private get _allowDecimals() {
    return this.fractionDigits() > 0;
  }

  ngOnInit(): void {
    this.numberControl = new FormControl(null, { updateOn: 'blur' });
    this.numberControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((v) => {
      this._onInnerValueChanged(v);
    });
    this._initializeRegEx();
    this._calculateRegularExpression();
  }

  ngAfterViewInit() {
    fromEvent<KeyboardEvent>(this.theInput().nativeElement, 'keydown')
      .pipe(
        takeUntil(this._destroyed$),
        tap((t: KeyboardEvent) => {
          this._onKeyDown(t);
        }),
      )
      .subscribe();

    fromEvent<PasteEvent>(this.theInput().nativeElement, 'paste')
      .pipe(
        takeUntil(this._destroyed$),
        tap((t: PasteEvent) => {
          this._onPaste(t);
        }),
      )
      .subscribe();

    // Disable clipboard cut event to prevent invalid values as a result
    fromEvent<Event>(this.theInput().nativeElement, 'cut')
      .pipe(
        takeUntil(this._destroyed$),
        tap((e: Event) => {
          e.preventDefault();
        }),
      )
      .subscribe();
  }

  private _onInnerValueChanged(v: any): void {
    const formattedValue = this._format(v, true);
    if (formattedValue !== v) {
      this.numberControl.setValue(formattedValue, { emitEvent: false, onlySelf: true });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        // using a switch in place of if to keep it extensible since this component can have
        // more inputs.
        switch (propName) {
          case 'fractionDigits':
            this._initializeRegEx();
            this._calculateRegularExpression();
            break;
          default:
            // do nothing
            break;
        }
      }
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  onChange: any = () => {
    //
  };

  onTouch: any = () => {
    //
  };

  onInputBlur: any = (event: any) => {
    const newValue = this.theInput().nativeElement.value;
    if (this._isNullish(newValue) || (String(newValue).match(this._regex) && this._isValueInRange(newValue))) {
      this._previousValue = this.innerValue = this._format(newValue);
      if (this._isNullish(newValue)) {
        this._onInnerValueChanged(newValue);
      }
    } else {
      this.numberControl.setValue(this._previousValue, { emitEvent: true });
      this._messageService.add({
        severity: 'error',
        summary: '',
        detail: `Invalid value of "${newValue}" entered. Reverting to previous value of "${this._previousValue}"`,
        life: 5000,
        closable: true,
      });
    }
    this.onBlur.emit(event);
  };

  set value(val) {
    this._previousValue = this.innerValue = this._format(val);
    this.numberControl.setValue(this.innerValue, { emitEvent: false });
  }

  // programmatically writing the value
  writeValue(value: any) {
    // Round value to fractionDigits if roundToFractionDigits enabled
    const fractionDigits = this.fractionDigits();
    if (this.roundToFractionDigits() && fractionDigits && value && !isNaN(value)) {
      value = +(+value).toFixed(fractionDigits);
    }
    this.value = value; // NOTE: calls the setter
  }
  // method to be triggered on UI change
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  // method to be triggered on component touch
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.disabled
      ? this.numberControl.disable({ emitEvent: false, onlySelf: true })
      : this.numberControl.enable({ emitEvent: false, onlySelf: true });
  }

  private _format(value: any, triggerOnChange = false): any {
    value = value?.toString();

    if (isNaN(value)) {
      return null;
    }

    if (value !== this._previousValue && (value === '' || value === null || value === undefined)) {
      if (triggerOnChange) {
        this.onChange(this._blankValue);
        this.onTouch(this._blankValue);
      }

      return value;
    }
    // when a numbers begins with a decimal separator,
    // fix it adding a zero in the beginning
    // e.g. user typed .23, convert it to 0.23
    let firstCharacter = value.charAt(0);
    if (firstCharacter === this._decimalSeparator) {
      value = 0 + value;
    }

    // when a numbers ends with a decimal separator,
    // fix it adding a zero in the end
    // example : user typed 3. -> 3.000(3 fraction digits)
    const lastCharacter = value.charAt(value.length - 1);
    if (lastCharacter === this._decimalSeparator) {
      for (let i = 0; i < this.fractionDigits(); i++) {
        value = value + 0;
      }
    }

    //remove unnececary leading zeros
    let signedValue = false;
    if (firstCharacter === this._minusSign) {
      //test for 0's without the sing interuption (avoid -004)
      signedValue = true;
      value = value.substring(1);
      firstCharacter = value.charAt(0);
    }
    let secondChar = value.charAt(1);
    while (firstCharacter === '0' && secondChar !== '' && secondChar !== this._decimalSeparator) {
      value = value.substring(1);
      firstCharacter = value.charAt(0);
      secondChar = value.charAt(1);
    }
    if (signedValue === true) {
      //return the minus value if required
      value = this._minusSign + value;
    }

    //test for excess zeros following the decimal point
    const valueParts = value.split(this._decimalSeparator);
    const naturalPart = valueParts?.[0];
    let decimalPart = valueParts?.[1];

    //remove unnececary zeros after decimal part
    if (this._allowDecimals && decimalPart !== null && decimalPart !== undefined && decimalPart.length < this.fractionDigits()) {
      for (let i = decimalPart.length; i < this.fractionDigits(); i++) {
        decimalPart += '0';
      }
      value = `${naturalPart}.${decimalPart}`;
    }

    //test for -0.0 or -0
    if (value === '-0') {
      value = '0';
    }
    if (value === '-0.0') {
      value = '0.0';
    }

    // test number with regular expression, when
    // number is invalid, replace it with a zero
    const valid = this._regex.test(value);
    let formattedValue = valid ? value : this._previousValue;
    if (formattedValue === '') {
      // accounts for user clearing out the input,
      // in this case we get "" but +"" = 0 ¯\_(ツ)_/¯
      formattedValue = this._blankValue;
    } else {
      formattedValue = (+formattedValue).toFixed(this.fractionDigits());
    }
    if (triggerOnChange && valid) {
      this.onChange(formattedValue);
      this.onTouch(formattedValue);
    }
    return formattedValue;
  }

  /**
   * Event handler for host's keydown event
   * @param event
   */
  private _onKeyDown(e: KeyboardEvent) {
    const key: string = this._getName(e);
    const controlOrCommand = e.ctrlKey === true || e.metaKey === true;

    // allowed keys apart from numeric characters
    const allowedKeys = [
      'ArrowLeft',
      'ArrowRight',
      'Escape',
      'Tab',
      'Home',
      'Shift',
      'End',
      'F1',
      'F2',
      'F3',
      'F4',
      'F5',
      'F6',
      'F7',
      'F8',
      'F9',
      'F10',
      'F11',
      'F12',
    ];

    // allow some non-numeric characters
    if (
      allowedKeys.indexOf(key) !== -1 ||
      controlOrCommand ||
      // Allow: Ctrl+A and Command+A
      (key === 'a' && controlOrCommand) ||
      // Allow: Ctrl+C and Command+C
      (key === 'c' && controlOrCommand) ||
      // Allow: Ctrl+V and Command+V
      (key === 'v' && controlOrCommand) ||
      // Allow: Ctrl+X and Command+X
      (key === 'x' && controlOrCommand)
    ) {
      // const it happen, don't do anything
      return;
    }

    this._clearSelectionIfAllSelected();

    // allow number characters only
    const isAllowed = this._allowedSymbols.findIndex((s) => s.toString() === key) !== -1;
    if (isAllowed) {
      const currentValue: string = this.theInput().nativeElement.value;
      const nextValueWithKey = this._getNextValueWithKey(currentValue, e.key);

      if (this._isNullish(nextValueWithKey) || (String(nextValueWithKey).match(this._regex) && this._isValueInRange(nextValueWithKey))) {
        // Accept the key (fall through to default return)
      } else {
        e.preventDefault();
      }
    } else {
      e.preventDefault();
    }
  }

  private _getNextValueWithKey(currentValue: string, key: string): string {
    const selectionStart = this.theInput().nativeElement.selectionStart;
    const selectionEnd = this.theInput().nativeElement.selectionEnd;

    switch (key) {
      case 'Decimal':
        return [currentValue.slice(0, selectionStart), '.', currentValue.slice(selectionEnd)].join('');
      case 'Backspace':
        if (selectionStart === selectionEnd) {
          return [currentValue.slice(0, selectionStart ? selectionStart - 1 : 0), currentValue.slice(selectionEnd)].join('');
        } else {
          return [currentValue.slice(0, selectionStart), currentValue.slice(selectionEnd)].join('');
        }
      case 'Delete':
        if (selectionStart === selectionEnd) {
          return [currentValue.slice(0, selectionStart), currentValue.slice(selectionEnd + 1)].join('');
        } else {
          return [currentValue.slice(0, selectionStart), currentValue.slice(selectionEnd)].join('');
        }
      default:
        return [currentValue.slice(0, selectionStart), key, currentValue.slice(selectionEnd)].join('');
    }
  }

  private _isValueInRange(nextValueWithKey: string) {
    // . Or - are valid values with in range
    if ((this._allowDecimals && nextValueWithKey === '.') || (this._allowSign && nextValueWithKey === '-')) {
      return true;
    }
    // floating point comparison is always a bit wierd.
    // This fix tries to get a good workable approximation
    const epsilon = Math.pow(10, -((this.fractionDigits() || 3) /** Most used value in Clear 2 */));
    let isMoreThanMin = true,
      isLessThanMax = true;
    const min = this.min();
    if (min !== null && typeof min !== 'undefined') {
      isMoreThanMin = +nextValueWithKey > +min - epsilon;
    }
    const max = this.max();
    if (min !== null && typeof max !== 'undefined') {
      isLessThanMax = +nextValueWithKey < +max + epsilon;
    }
    return isMoreThanMin && isLessThanMax;
  }

  /**
   * _clearSelectionIfAllSelected handles the situation when the user selects the whole text
   * and starts typing. Still not handling the situation when only partial is selected!
   */
  private _clearSelectionIfAllSelected() {
    const selectedText = this._getSelectedText();
    const currentValue = this.theInput().nativeElement.value;
    if (selectedText && currentValue && selectedText === currentValue) {
      this.theInput().nativeElement.value = '';
      this.numberControl.setValue('', { emitEvent: false });
    }
  }

  private _getSelectedText() {
    return this.theInput().nativeElement.value.substring(this.theInput().nativeElement.selectionStart, this.theInput().nativeElement.selectionEnd);
  }

  /**
   * Get key's name
   * @param e
   */
  private _getName(e: any): string {
    if (e.key) {
      return e.key;
    } else {
      // for old browsers
      if (e.keyCode && String.fromCharCode) {
        switch (e.keyCode) {
          case 8:
            return 'Backspace';
          case 9:
            return 'Tab';
          case 27:
            return 'Escape';
          case 37:
            return 'ArrowLeft';
          case 39:
            return 'ArrowRight';
          case 188:
            return ',';
          case 190:
            return '.';
          case 109:
            return '-'; // minus in numbpad
          case 173:
            return '-'; // minus in alphabet keyboard in firefox
          case 189:
            return '-'; // minus in alphabet keyboard in chrome
          default:
            return String.fromCharCode(e.keyCode);
        }
      }
      return String.fromCharCode(e.keyCode);
    }
  }

  private _initializeRegEx() {
    this._integerUnsigned = new RegExp('^[0-9]*$');
    this._integerSigned = new RegExp('^-?[0-9]+$');
    this._decimalUnsigned = new RegExp(`^\\d*(\\.\\d{${this._minFractionDigits},${this.fractionDigits()}})?$`);
    this._decimalSigned = new RegExp(`^-?\\d*(\\.\\d{${this._minFractionDigits},${this.fractionDigits()}})?$`);
  }

  /**
   * Event handler for host's paste event
   * @param e
   */
  private _onPaste(e: PasteEvent) {
    e.preventDefault();
  }

  private _calculateRegularExpression() {
    this._regex = this._integerUnsigned;
    if (!this._allowDecimals && !this._allowSign) {
      this._regex = this._integerUnsigned;
    }
    if (!this._allowDecimals && this._allowSign) {
      this._regex = this._integerSigned;
    }
    if (this._allowDecimals && !this._allowSign) {
      this._regex = this._decimalUnsigned;
    }
    if (this._allowDecimals && this._allowSign) {
      this._regex = this._decimalSigned;
    }
  }

  private _isNullish<T>(value: T | null | undefined | ''): value is null | undefined | '' {
    return value === null || value === undefined || value === '';
  }
}
